import React, { useState, useEffect } from "react";
import { Grid, Divider, Drawer } from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import Skeleton from "@material-ui/lab/Skeleton";
import clsx from "clsx";
import RefundTransactionSearch from "./RefundTransactionSearch/RefundTransactionSearch";
import RefundTransactionList from "./RefundTransactionList/RefundTransactionList";
import RefundTransactionService from "../../services/RefundTransactionService";
import useCurrentFacility from "../../hooks/useCurrentFacility";
import { useEnqueueSnackbar } from "../../hooks/useEnqueueSnackbar";
import useCancellationToken from "../../hooks/useCancellationToken";
import apiClient from "../../auth/apiClient";
import { useStyles } from "./RefundTransactions.styles";
import RefundDetails from "./RefundDetails/RefundDetails";

const refundTransactionService = new RefundTransactionService(apiClient);

const RefundTransactions = () => {
  const classes = useStyles();
  const { facilityID: entityID } = useCurrentFacility();
  const enqueueSnackbar = useEnqueueSnackbar();
  const pageSize = 5;

  const [searchResults, setSearchResults] = useState({
    collection: [],
    totalCount: 0,
  });
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams, setSearchParams] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showRefundDetails, setShowRefundDetails] = useState(false);
  const [drawerCloseHandler, setDrawerCloseHandler] = useState(null);
  const [searchResetKey, setSearchResetKey] = useState(0);

  const executeSearch = useCancellationToken({
    func: async ({ cancelToken }) => {
      if (!searchParams) return;

      setLoading(true);
      try {
        const offset = (currentPage - 1) * pageSize;
        const response = await refundTransactionService.searchRefundTransactions(
          {
            entityID: entityID,
            ticketNumber: searchParams.ticketNumber,
            cardLast4: searchParams.cardLast4,
            transactionID: searchParams.transactionID,
            startTransactionDateTime: searchParams.startTransactionDateTime,
            endTransactionDateTime: searchParams.endTransactionDateTime,
            limit: pageSize,
            offset,
          },
          cancelToken
        );

        setHasSearched(true);
        setSearchResults(response.data || { collection: [], totalCount: 0 });
        setSelectedTransaction(null);
      } catch (error) {
        enqueueSnackbar("Failed to search refund transactions", {
          variant: "error",
          tag: "FailedToSearchRefundTransactions",
        });
        setSearchResults({ collection: [], totalCount: 0 });
        setHasSearched(true);
      } finally {
        setLoading(false);
      }
    },
  }).execute;

  const onSearch = (searchParamsData) => {
    setSearchParams(searchParamsData);
    setCurrentPage(1);
    setSelectedTransaction(null);
  };

  const handleSelectedTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setShowRefundDetails(true);
  };

  useEffect(() => {
    if (searchParams) {
      executeSearch();
    }
  }, [entityID, currentPage, searchParams]);

  const handleDrawerClose = (isRefundSuccessful = false) => {
    setShowRefundDetails(false);

    if (isRefundSuccessful) {
      setShowRefundDetails(false);
      setSelectedTransaction(null);
      setSearchResults({ collection: [], totalCount: 0 });
      setHasSearched(false);
      setLoading(false);
      setSearchParams(null);
      setSearchResetKey((prev) => prev + 1);
    }
  }

  return (
    <>
        <Grid container className={clsx(classes.root)}>
          <RefundTransactionSearch key={searchResetKey} onSearch={onSearch} loading={loading} />

          <Grid container className={clsx(classes.infoContainer)}>
            <Grid item xs={12}>
              {loading ? (
                <div className={clsx(classes.loadingWrapper)}>
                  {[...Array(3)].map((_, index) => (
                    <Skeleton
                      key={index}
                      className={clsx(classes.skeleton)}
                      animation="wave"
                    />
                  ))}
                </div>
              ) : (
                hasSearched && (
                  <>
                    <Divider className={clsx(classes.resultsDivider)} />
                  </>
                )
              )}
            </Grid>
          </Grid>

          <Grid
            container
            item
            spacing={2}
            xs={12}
            className={clsx(classes.tableContainer)}
          >
            {!loading && hasSearched && searchResults.totalCount > 0 && (
              <>
                <Pagination
                  className={clsx(classes.pagination)}
                  count={Math.ceil(searchResults.totalCount / pageSize)}
                  onChange={(_, value) => setCurrentPage(value)}
                  page={currentPage}
                  shape="rounded"
                  color="primary"
                />
                <RefundTransactionList
                  results={searchResults.collection}
                  onSelectTransaction={handleSelectedTransaction}
                />
              </>
            )}
            {!loading && hasSearched && searchResults.totalCount === 0 && (
              <RefundTransactionList
                results={[]}
                onSelectTransaction={handleSelectedTransaction}
              />
            )}
          </Grid>
        </Grid> 

      <Drawer
        anchor="right"
        open={showRefundDetails}
        onClose={() => {
          if (drawerCloseHandler) {
            drawerCloseHandler();
          }
        }}
        classes={{ paper: classes.drawerPaper }}
      >
        {showRefundDetails && selectedTransaction && (
          <div className={classes.drawerContent}>
            <RefundDetails
              key={selectedTransaction.parkingTransactionID}
              parkingTransactionId={selectedTransaction.parkingTransactionID}
              onDrawerClose={handleDrawerClose}
              setDrawerCloseHandler={setDrawerCloseHandler}
            />
          </div>
        )}
      </Drawer>
    </>
  );
};

export default RefundTransactions;
